import BankOutlined from '@ant-design/icons/BankOutlined'
import CloseOutlined from '@ant-design/icons/CloseOutlined'
import EditOutlined from '@ant-design/icons/EditOutlined'
import TeamOutlined from '@ant-design/icons/TeamOutlined'
import UserOutlined from '@ant-design/icons/UserOutlined'
import {
  Button,
  Col,
  Form,
  Modal,
  Row,
  Space,
  Tag,
  Tooltip,
  message,
} from 'antd'
import { inject, observer } from 'mobx-react'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { blue } from '../../../color'
import { AvatarHasName } from '../../../components/Common/Avatar'
import SelectPeoplePopup from '../../../components/SelectPeoplePopup'
import { ASSIGNEE_TYPE } from '../../../constants'
import { SelectList } from '../InternalDocumentOutgoingDocCreatePage/InternalDocumentOutgoingDocCreatePageStyled'
import {
  getBoardTypeByTab,
  getFeedbackTabFromData,
} from './feedbackBoardType'

const FeedbackBoardAddPerson = ({
  handleCancel,
  isVisible,
  feedbackDetailData,
  selectPeopleStore,
  FeedbackProcedureStore,
  loadingAnimationStore,
  fetchFeedback,
  setIsVisible,
}) => {
  const {
    selectUserData,
    selectDepartmentData,
    selectGroupData,
    isVisibleSelectUserDepartmentPopup,
    setIsVisibleSelectUserDepartmentPopup,
    handleOpenSelectUserDepartmentPopup,
    handleCancelSelectUserDepartment,
    originDepartmentWithUserList,
    clearSelected,
    clearSelectUserData,
    originGroupList,
  } = selectPeopleStore

  const [handlerList, setHandlerList] = useState([])
  const [followerList, setFollowerList] = useState([])
  const [combinerList, setCombinerList] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    ;(async () => {
      await clearSelectUserData()
      await clearSelected()
    })()
  }, [])

  const assignees = feedbackDetailData?.assignees || []

  const userList = useMemo(() => {
    return assignees.filter(
      el =>
        el.assignee_type === ASSIGNEE_TYPE.USER &&
        [
          ASSIGNEE_TYPE.HANDLER,
          ASSIGNEE_TYPE.FOLLOWER,
          ASSIGNEE_TYPE.COMBINER,
        ].includes(el.permission)
    )
  }, [assignees])

  const departmentList = useMemo(() => {
    return assignees.filter(
      el =>
        el.assignee_type === ASSIGNEE_TYPE.DEPARTMENT &&
        [
          ASSIGNEE_TYPE.HANDLER,
          ASSIGNEE_TYPE.FOLLOWER,
          ASSIGNEE_TYPE.COMBINER,
        ].includes(el.permission)
    )
  }, [assignees])

  const groupList = useMemo(() => {
    return assignees.filter(
      el =>
        el.assignee_type === ASSIGNEE_TYPE.GROUP &&
        [
          ASSIGNEE_TYPE.HANDLER,
          ASSIGNEE_TYPE.FOLLOWER,
          ASSIGNEE_TYPE.COMBINER,
        ].includes(el.permission)
    )
  }, [assignees])

  useEffect(() => {
    if (isVisible) {
      selectPeopleStore.setSelectUserData(
        selectPeopleStore.convertSelectDataObj(userList)
      )
      selectPeopleStore.setSelectDepartmentData(
        selectPeopleStore.convertSelectDataObj(departmentList)
      )
      selectPeopleStore.setSelectGroupData(
        selectPeopleStore.convertSelectDataObj(groupList)
      )
    }
  }, [isVisible, originDepartmentWithUserList])

  useEffect(() => {
    const combinedHandlerList = [
      ...selectUserData[ASSIGNEE_TYPE.HANDLER],
      ...selectDepartmentData[ASSIGNEE_TYPE.HANDLER],
      ...selectGroupData[ASSIGNEE_TYPE.HANDLER],
    ]
    setHandlerList(combinedHandlerList)

    const combinedFollowerList = [
      ...selectUserData[ASSIGNEE_TYPE.FOLLOWER],
      ...selectDepartmentData[ASSIGNEE_TYPE.FOLLOWER],
      ...selectGroupData[ASSIGNEE_TYPE.FOLLOWER],
    ]
    setFollowerList(combinedFollowerList)

    const combinedCombinerList = [
      ...selectUserData[ASSIGNEE_TYPE.COMBINER],
      ...selectDepartmentData[ASSIGNEE_TYPE.COMBINER],
      ...selectGroupData[ASSIGNEE_TYPE.COMBINER],
    ]
    setCombinerList(combinedCombinerList)
  }, [selectUserData, selectDepartmentData, selectGroupData])

  const onFinish = async () => {
    setIsSubmitting(true)
    try {
      const handlerData = handlerList.map(el => ({
        assignee_code: el.id,
        assignee_type: el.assignee_type,
        permission: ASSIGNEE_TYPE.HANDLER,
      }))
      const followerData = followerList.map(el => ({
        assignee_code: el.id,
        assignee_type: el.assignee_type,
        permission: ASSIGNEE_TYPE.FOLLOWER,
      }))
      const combinerData = combinerList.map(el => ({
        assignee_code: el.id,
        assignee_type: el.assignee_type,
        permission: ASSIGNEE_TYPE.COMBINER,
      }))

      // Preserve other assignees (like CREATOR, VIEW, etc.)
      const otherAssignees = assignees
        .filter(
          a =>
            ![
              ASSIGNEE_TYPE.HANDLER,
              ASSIGNEE_TYPE.FOLLOWER,
              ASSIGNEE_TYPE.COMBINER,
            ].includes(a.permission)
        )
        .map(a => ({
          assignee_code: a.assignee_code,
          assignee_type: a.assignee_type,
          permission: a.permission,
        }))

      const submitData = {
        id: feedbackDetailData.id,
        title: feedbackDetailData.title,
        boardType: getBoardTypeByTab(
          getFeedbackTabFromData({
            boardType: feedbackDetailData.boardType || feedbackDetailData.board_type,
            documentNumber: feedbackDetailData.documentNumber,
          })
        ),
        documentNumber: feedbackDetailData.documentNumber,
        startDate: feedbackDetailData.startDate,
        endDate: feedbackDetailData.endDate,
        legalBasis: feedbackDetailData.legalBasis || '',
        purpose: feedbackDetailData.purpose || '',
        attachedDocuments: feedbackDetailData.attachedDocuments || '',
        otherContent: feedbackDetailData.otherContent || '',
        content: feedbackDetailData.content || '',
        fileIdAttachment: {
          removeItems: [],
          newItems: [],
        },
        status: feedbackDetailData.status || 'PENDING',
        isTTr: feedbackDetailData.isTTr || false,
        assignees: [
          ...otherAssignees,
          ...handlerData,
          ...followerData,
          ...combinerData,
        ],
        feedbackComments: (feedbackDetailData.feedbackComments || []).map(
          item => ({
            id: item.id,
            content: item.content,
          })
        ),
      }

      const response = await FeedbackProcedureStore.updateFeedbackProcedure(
        submitData,
        feedbackDetailData.id
      )

      if (response.status === 200) {
        setIsVisible(false)
        message.success('Cập nhật người xử lý thành công')
        fetchFeedback()
      }
    } catch (error) {
      console.error(error)
      message.error('Đã có lỗi xảy ra!')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleOpenHandler = () =>
    handleOpenSelectUserDepartmentPopup(ASSIGNEE_TYPE.HANDLER)
  const handleOpenFollower = () =>
    handleOpenSelectUserDepartmentPopup(ASSIGNEE_TYPE.FOLLOWER)
  const handleOpenCombiner = () =>
    handleOpenSelectUserDepartmentPopup(ASSIGNEE_TYPE.COMBINER)
  const handleCloseSelectUser = () =>
    setIsVisibleSelectUserDepartmentPopup(false)

  const renderTags = list => {
    return list.map(el => (
      <Tag
        className={'tag-selected'}
        color={'blue'}
        style={{ marginTop: '10px' }}
        key={el.id}
        closable
        onClose={() => selectPeopleStore.handleRemoveSelect(el)}>
        <AvatarHasName
          imgId={el.image_id}
          size={22}
          name={el.full_name || el.name_uppercase}
          icon={
            (el.assignee_type === ASSIGNEE_TYPE.DEPARTMENT && (
              <BankOutlined />
            )) ||
            (el.assignee_type === ASSIGNEE_TYPE.GROUP && <TeamOutlined />)
          }
        />
      </Tag>
    ))
  }

  return (
    <>
      <Modal
        title="Thêm người"
        visible={isVisible}
        onCancel={handleCancel}
        footer={null}
        width={800}
        maskClosable={false}>
        <Form layout="vertical" onFinish={onFinish}>
          <Row gutter={[24, 16]}>
            <Col span={24}>
              <span style={{ marginRight: '10px', fontWeight: 500 }}>
                Người xử lý
              </span>
              <Space>
                <Tooltip title="Chọn người dùng" color={blue}>
                  <Button
                    icon={<UserOutlined style={{ color: blue }} />}
                    onClick={handleOpenHandler}
                  />
                </Tooltip>
              </Space>
              <SelectList>{renderTags(handlerList)}</SelectList>
            </Col>
            <Col span={24}>
              <span style={{ marginRight: '10px', fontWeight: 500 }}>
                Ý kiến của Ban KS/Ban KTNB
              </span>
              <Space>
                <Tooltip title="Chọn người dùng" color={blue}>
                  <Button
                    icon={<UserOutlined style={{ color: blue }} />}
                    onClick={handleOpenFollower}
                  />
                </Tooltip>
              </Space>
              <SelectList>{renderTags(followerList)}</SelectList>
            </Col>
            <Col span={24}>
              <span style={{ marginRight: '10px', fontWeight: 500 }}>
                Người theo dõi
              </span>
              <Space>
                <Tooltip title="Chọn người dùng" color={blue}>
                  <Button
                    icon={<UserOutlined style={{ color: blue }} />}
                    onClick={handleOpenCombiner}
                  />
                </Tooltip>
              </Space>
              <SelectList>{renderTags(combinerList)}</SelectList>
            </Col>
          </Row>
          <Form.Item style={{ margin: 0, width: '100%', marginTop: '30px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px',
              }}>
              <Button danger icon={<CloseOutlined />} onClick={handleCancel}>
                Hủy bỏ
              </Button>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={onFinish}
                loading={isSubmitting}>
                Cập nhật
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
      <SelectPeoplePopup
        handleCancelSelectUser={handleCancelSelectUserDepartment}
        handleSubmitSelectUser={handleCloseSelectUser}
        isVisibleSelectPeoplePopup={isVisibleSelectUserDepartmentPopup}
      />
    </>
  )
}

export default inject(
  'loadingAnimationStore',
  'selectPeopleStore',
  'FeedbackProcedureStore'
)(observer(FeedbackBoardAddPerson))
